/**
 * Infinite Scroll for Shopify Collections
 * Loads the next page when the end of the grid comes within reach.
 *
 * No scroll listener and no measured page height. An IntersectionObserver
 * watches a one-pixel sentinel placed after the grid, with the lead distance
 * as the observer's bottom margin: the browser reports when the sentinel is
 * within that distance of the viewport and re-evaluates on its own as lazy
 * images grow the page or a page is appended. The version before this read a
 * cached document height against innerHeight + scrollY on every scroll frame
 * and re-measured when the gate tripped; the cache went stale as images
 * loaded, and the listener sat in the scroll path on slow phones. Nothing
 * here reads a viewport number.
 */

class InfiniteScroll {
  constructor() {
    this.container = document.querySelector('[data-infinite-scroll]');
    if (!this.container) return;

    this.productGrid = document.getElementById('product-grid');
    this.loader = document.querySelector('.infinite-scroll-loader');
    this.endMessage = document.querySelector('.infinite-scroll-end');

    this.currentPage = parseInt(this.container.dataset.currentPage);
    this.totalPages = parseInt(this.container.dataset.totalPages);
    this.sectionId = this.container.dataset.sectionId;
    this.isLoading = false;

    this.init();
  }

  init() {
    // Only initialize if there are more pages
    if (this.currentPage >= this.totalPages || !this.productGrid) {
      return;
    }

    /* No forced scroll-to-top on back-navigation.
       It used to call window.scrollTo(0, 0) here, which did three things wrong:
       it threw away the browser's own scroll restoration (on an infinitely
       scrolled collection that means losing your place entirely), html has
       scroll-behavior:smooth so the bare two-argument call ANIMATED rather
       than jumping, and being unguarded it fed the header's direction
       detector a large delta. It also read performance.navigation.type,
       removed from the spec. Letting the browser restore the position is
       both correct and free. */

    /* The sentinel: one pixel after the grid, never painted over anything.
       When a page is appended the grid grows and the sentinel moves down with
       its end, so it always marks the place the next page should follow. */
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    /* display:block inline: base.css hides every empty div (Dawn's :empty
       rule), and an unobservable sentinel loads nothing — found in the
       Simulator, where the observer never fired. */
    this.sentinel.style.cssText = 'display:block;height:1px;margin:0;padding:0;pointer-events:none';
    this.productGrid.insertAdjacentElement('afterend', this.sentinel);
    this.observe();

    /* The lead is a rootMargin, fixed when the observer is made; a rotation
       across the breakpoint gets a fresh one. */
    this.onResize = () => {
      if (this.lead() !== this.leadUsed) this.observe();
    };
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  /* How far below the viewport the end of the grid may be before the next
     page is fetched. A phone gets the longer lead so the fetch lands before
     the thumb does. */
  lead() {
    return window.innerWidth < 768 ? 1200 : 800;
  }

  observe() {
    if (this.observer) this.observer.disconnect();
    this.leadUsed = this.lead();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: `0px 0px ${this.leadUsed}px 0px` }
    );
    this.observer.observe(this.sentinel);
  }

  /* observe() delivers an entry for the current state, so re-observing after
     an append asks the question again. On a tall viewport a freshly appended
     page can leave the sentinel still inside the lead, and an observer only
     speaks when the answer CHANGES. */
  rearm() {
    if (!this.observer || !this.sentinel) return;
    this.observer.unobserve(this.sentinel);
    this.observer.observe(this.sentinel);
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.remove();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  async loadNextPage() {
    // Don't load if already loading or all pages loaded
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.isLoading = true;
    this.showLoader();

    try {
      const nextPage = this.currentPage + 1;

      // Build URL with current filters/sorting + next page
      const url = this.buildNextPageUrl(nextPage);

      const response = await fetch(url);
      const html = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get the new products
      const newProducts = doc.querySelectorAll('#product-grid .grid__item');

      if (newProducts.length > 0) {
        // Append new products to existing grid
        newProducts.forEach(product => {
          this.productGrid.appendChild(product);
        });

        // Notify the client-side weight-sort in filter-drawer.js to
        // re-apply, since new products were just appended to the grid.
        // Weight sort is necessarily client-side (Liquid can't paginate
        // a sorted array). See main-collection-product-grid.liquid for
        // the full constraint explanation.
        document.dispatchEvent(new CustomEvent('productGridUpdated'));

        // Update current page
        this.currentPage = nextPage;
        this.container.dataset.currentPage = nextPage;

        // Check if this was the last page
        if (this.currentPage >= this.totalPages) {
          this.showEndMessage();
        }
      }
    } catch (error) {
      console.error('Error loading next page:', error);
    } finally {
      this.isLoading = false;
      this.hideLoader();
      if (this.currentPage >= this.totalPages) this.destroy();
      else this.rearm();
    }
  }

  buildNextPageUrl(page) {
    // Get the base collection URL
    const baseUrl = window.location.pathname;

    // Clone current URL params
    const params = new URLSearchParams(window.location.search);

    // Set the page parameter
    params.set('page', page);

    // Add section_id for AJAX loading
    params.set('section_id', this.sectionId);

    return `${baseUrl}?${params.toString()}`;
  }

  showLoader() {
    if (this.loader) {
      this.loader.classList.add('loading');
    }
  }

  hideLoader() {
    if (this.loader) {
      this.loader.classList.remove('loading');
    }
  }

  showEndMessage() {
    if (this.endMessage) {
      this.endMessage.classList.add('visible');
    }
  }
}

/* One live instance. A filter change swaps in a fresh grid and dispatches
   infiniteScrollReset (filter-drawer.js); the old instance is torn down
   first. Before, its scroll listener lived on against a detached grid and
   kept fetching pages nobody could see. */
let currentInfiniteScroll = null;
const startInfiniteScroll = () => {
  if (currentInfiniteScroll) currentInfiniteScroll.destroy();
  currentInfiniteScroll = new InfiniteScroll();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startInfiniteScroll);
} else {
  startInfiniteScroll();
}

// Re-initialize after AJAX filter updates
document.addEventListener('infiniteScrollReset', startInfiniteScroll);
